import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button } from 'react-bootstrap';
import { useLocation } from 'react-router-dom';
import {
  buildVersionUrl,
  isSafeReloadPath,
  isValidReleasePayload,
  VERSION_CHECK_INTERVAL_MS,
} from './release-version.utils';
import './version-update.styles.scss';

const defaultFetch = (...args) => window.fetch(...args);
const defaultReload = () => window.location.reload();

const VersionUpdateGuard = ({
  children,
  currentVersion = process.env.REACT_APP_RELEASE_VERSION,
  enabled = process.env.NODE_ENV === 'production',
  intervalMs = VERSION_CHECK_INTERVAL_MS,
  fetchImpl = defaultFetch,
  reloadPage = defaultReload,
}) => {
  const location = useLocation();
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const requestInFlight = useRef(false);
  const reloadRequested = useRef(false);

  const checkVersion = useCallback(async () => {
    if (!enabled || !currentVersion || !fetchImpl || requestInFlight.current) {
      return;
    }

    requestInFlight.current = true;

    try {
      const response = await fetchImpl(
        buildVersionUrl(process.env.PUBLIC_URL || ''),
        {
          cache: 'no-store',
          credentials: 'same-origin',
          headers: { Accept: 'application/json' },
        }
      );

      if (!response.ok) {
        return;
      }

      const release = await response.json();
      if (
        isValidReleasePayload(release) &&
        release.version.trim() !== currentVersion.trim()
      ) {
        setUpdateAvailable(true);
      }
    } catch (error) {
      // A failed version check must never interrupt the current user session.
    } finally {
      requestInFlight.current = false;
    }
  }, [currentVersion, enabled, fetchImpl]);

  useEffect(() => {
    if (!enabled || !currentVersion) {
      return undefined;
    }

    const checkWhenVisible = () => {
      if (document.visibilityState === 'visible') {
        checkVersion();
      }
    };
    const intervalId = window.setInterval(checkWhenVisible, intervalMs);

    checkVersion();
    window.addEventListener('focus', checkWhenVisible);
    window.addEventListener('online', checkWhenVisible);
    document.addEventListener('visibilitychange', checkWhenVisible);

    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener('focus', checkWhenVisible);
      window.removeEventListener('online', checkWhenVisible);
      document.removeEventListener('visibilitychange', checkWhenVisible);
    };
  }, [checkVersion, currentVersion, enabled, intervalMs]);

  const requestReload = useCallback(() => {
    if (!reloadRequested.current) {
      reloadRequested.current = true;
      reloadPage();
    }
  }, [reloadPage]);

  useEffect(() => {
    if (updateAvailable && isSafeReloadPath(location.pathname)) {
      requestReload();
    }
  }, [location.pathname, requestReload, updateAvailable]);

  return (
    <>
      {children}
      {updateAvailable && !isSafeReloadPath(location.pathname) && (
        <div className="version-update-banner" role="status" aria-live="polite">
          <span>A new version of PetroCard is available.</span>
          <Button size="sm" variant="light" onClick={requestReload}>
            Reload now
          </Button>
        </div>
      )}
    </>
  );
};

export default VersionUpdateGuard;
